// The homestead: every home along a spiral, oldest at the centre, each on its
// own floating island, as a living map. Stepping stones run between
// neighbours; friends who live within two plots have a rope bridge. Plazmi who
// are home stand by their doors (or on a friend's lawn), windows are lit when
// someone's in, and music floats up from a home where someone's singing or
// dancing. Hover a home for a card; click to visit. The light follows world
// time, as on a house page (daylight.ts): dawn to night, stars over the spiral.
// GET /homestead loads this; ?still makes one frame (golden hour, unless
// ?time=21 says otherwise).
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { capView } from './viewdist.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { setStyle } from './kit.js';
import { makeRenderer, makeStage, makeCamera, clouds } from './stage.js';
import { makeComposer } from './post.js';
import { fitView } from './fit.js';
import { govern, startPR, watchFrom, mountGraphics } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { homeSign, guide } from './door.js';
import { shell, type GoItem } from './ui/shell.js';
import { waysToGo } from './ui/places.js';
import { visit, visiting } from './arrive.js';
import { goneTo, startFollow, followElsewhere } from './follow.js';
import { outAndAboutTab, seeEveryone } from './nowlist.js';
import { Daylight, fixedHourFrom } from './daylight.js';
import { camControls, clickGuard } from './camctl.js';
import { sinceSpiral } from './since.js';
import { bakedMaterials, doorSpot, allMaterials, setHaze, setHazeColor, Folk, Notes, NightLights, lightSpots, type Bridge, type Isle } from './neighbourhood.js';
import { SpiralMap, fitShadow } from './spiralmap.js';

type Home = Isle & { resident: { name: string; emoji?: string; said?: string | null } };

const q = new URLSearchParams(location.search);
const still = q.has('still');
const ui = document.getElementById('ui') as HTMLElement;
const stats = { fps: 0, pr: 0, homes: 0, bridges: 0, triangles: 0, calls: 0, mergeMs: 0, bakeMs: 0 };
(window as any).__plazmi = stats;
function done() { (window as any).__ready = true; }
const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

async function main() {
  // (the page's head asked for the spiral already, alongside the bundle: loading.ts)
  const data = (await early('/api/homestead')) as { homes: Home[]; bridges: Bridge[]; summary: string; now: number; festivals?: { title: string; text: string; house_name?: string; kind: string }[] };
  loadStage(2);
  const homes = data.homes;
  let bridges = data.bridges || [];
  let now0 = data.now, wall0 = performance.now();
  const serverNow = () => now0 + (performance.now() - wall0);
  setStyle('A');
  let vw = innerWidth, vh = innerHeight; const dpr = devicePixelRatio || 1;
  const prCap = still ? Math.min(2, dpr) : Math.min(1.5, dpr);
  let PR = +(q.get('pr') || (still ? prCap : Math.min(prCap, startPR())));
  const renderer = makeRenderer(vw, vh, PR);
  renderer.info.autoReset = false; // counted over every pass of a frame (stats.calls)

  // the spiral's extent, from where the islands really float
  const box = new THREE.Box3();
  for (const h of homes) box.expandByPoint(new THREE.Vector3(h.pos[0], 0, h.pos[2]));
  if (!homes.length) box.expandByPoint(new THREE.Vector3(0, 0, 0));
  const target = box.getCenter(new THREE.Vector3()).setY(0);
  const span = Math.max(30, box.getSize(new THREE.Vector3()).length() / 2 + 14);
  const { scene, sun, hemi } = makeStage(renderer, { target, shadowExtent: span * 1.05, shadowSize: 4096, fogNear: span * 4, fogFar: span * 9 });
  if (!still) { renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true; }
  sun.shadow.camera.far = 400; sun.position.copy(target).addScaledVector(sun.position.clone().sub(target).normalize(), 160);
  sun.shadow.camera.updateProjectionMatrix();
  // a big spiral has bigger clouds further below, so they keep their size against it
  const cloudK = Math.max(1, Math.min(4, span / 250));
  const sky = clouds(target.x, -24, target.z, Math.max(140, span * 3.2) / cloudK, 30, 13);
  sky.scale.setScalar(cloudK);
  scene.add(sky);

  // frame every island: centre what the camera sees, and step back until it all fits
  // (climb round 7) a tall phone looks from higher up and lets the outer turn run off the sides, so a home is big enough to tap; a drag brings them in
  const tall = vh / vw > 1.3 && vw <= 600;
  const dir: [number, number, number] = tall ? [-0.35, 2.1, 1.0] : [-0.5, 1.15, 1.0];
  let dist = Math.max(90, span * 3.0);
  const cam = makeCamera(vw, vh, target, dir, dist);
  cam.far = 2000; cam.updateProjectionMatrix();
  const corners: THREE.Vector3[] = [];
  for (const h of homes) for (const [dx, dy, dz] of [[-1, 0, -1], [1, 0, -1], [-1, 0, 1], [1, 0, 1], [0, 7, 0], [0, -6, 0]]) {
    corners.push(new THREE.Vector3(h.pos[0] + (dx * h.plot[0]) / 2, h.pos[1] + dy, h.pos[2] + (dz * h.plot[1]) / 2));
  }
  const aim = (d: number) => { cam.position.copy(target).addScaledVector(new THREE.Vector3(...dir).normalize(), d); cam.lookAt(target); cam.updateMatrixWorld(true); };
  for (let k = 0; k < 40 && corners.length; k++) {
    aim(dist);
    const b = new THREE.Box2();
    for (const c of corners) { const p = c.clone().project(cam); b.expandByPoint(new THREE.Vector2(p.x, p.y)); }
    // nudge the target toward the middle of what's in view
    const mid = b.getCenter(new THREE.Vector2());
    const right = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 0).setY(0).normalize();
    const fwd = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 2).setY(0).normalize().negate();
    const scale = dist * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    target.addScaledVector(right, mid.x * scale * cam.aspect * 0.6).addScaledVector(fwd, mid.y * scale * 1.2);
    const fit = Math.max((b.max.x - b.min.x) / (tall ? 2.5 : 1.8), (b.max.y - b.min.y) / 1.62);
    if (Math.abs(fit - 1) < 0.02 && mid.length() < 0.03) break;
    dist *= Math.min(1.25, Math.max(0.8, fit));
  }
  dist = +(q.get('dist') || dist);
  aim(dist);
  // far enough to see the whole spiral from as far back as a visitor can step (1.8 × this framing, below)
  cam.far = Math.max(2000, dist * 1.8 + span * 4); cam.updateProjectionMatrix();
  const H = { near: dist * 0.9, far: dist * 2.6, max: 0.32 };
  // the fog starts past the spiral however far back a narrow screen stands the camera
  const fog = scene.fog as THREE.Fog; fog.near = Math.max(fog.near, dist * 1.3); fog.far = Math.max(fog.far, dist * 3.2);
  const mats = bakedMaterials(H);

  // ----- the islands (spiralmap.ts): the ones near the camera baked in full and merged in runs
  // of 16, every other one simply; within the tier's budget, all in full, as always
  const sp = new SpiralMap({ mats, minSize: 0.07, shadows: true });
  sp.setHomes(homes, bridges);
  scene.add(sp.root);
  const homeBy = sp.by;
  const atOf = (h: Home) => sp.placeAt(h);
  // a big spiral keeps the sun's shadow over the homes drawn in full, so it stays crisp
  const shade = () => {
    if (sp.whole) return;
    daylight.sunDist = fitShadow(sun, sp.nearBox());
    renderer.shadowMap.needsUpdate = true;
  };
  // after dark every home has its porch light and a lantern at the gate (one set for the whole spiral)
  const lights = new NightLights(3.6, 1.5);
  const lamps = new THREE.Group(); lamps.userData.noAO = true; lamps.userData.noShadow = true;
  scene.add(lamps);
  const placeLights = () => {
    for (const c of [...lamps.children]) { lamps.remove(c); c.traverse((x: any) => x.geometry?.dispose()); }
    lamps.add(lights.build(homes.map((h) => ({ spots: lightSpots(h), at: atOf(h) }))));
  };
  placeLights();
  // a still waits for every home it draws in full; a live page fills in from the centre out
  // (oldest first; nearest first on a big spiral), a home at a time, so the first frame never waits
  sp.choose(cam, true);
  if (still) { for (const h of [...sp.queue]) sp.bake(h); sp.merge(true); }
  else { for (let k = 0; k < 4 && sp.bake(); k++); sp.merge(true); }
  sp.lod(cam);
  stats.homes = homes.length;
  const ways = { set bridges(b: Bridge[]) { sp.setBridges(b); stats.bridges = b.length; } };
  ways.bridges = bridges;

  // ----- plazmi: at home by the door, or a guest on someone's lawn
  let hovered: string | null = null;
  const folk = new Folk(1.9, H);
  scene.add(folk.root);
  const notes = new Notes(2.2);
  scene.add(notes.root);
  const placeFolk = () => {
    const guests: Record<string, number> = {};
    const figs = [];
    // (only at the homes drawn in full: from further off a plazmi is too small to see)
    for (const h of homes) {
      const hs = h.at || h.slug, host = homeBy.get(hs);
      if (!host || !sp.near.has(hs)) continue;
      const k = h.at === h.slug ? -1 : (guests[host.slug] = (guests[host.slug] ?? -1) + 1);
      const [x, z] = doorSpot(host, k);
      const p = atOf(host as Home);
      const m = host.music && serverNow() - host.music.at < 120000;
      // a hop to greet whoever's pointing at its home
      const greet = hovered === h.slug && h.at === h.slug;
      figs.push({ slug: h.slug, color: h.color, glow: h.glow, x: p.x + x, y: p.y, z: p.z + z, yaw: -0.5 + (k < 0 ? 0 : 0.7), dancing: !!m || greet });
    }
    folk.set(figs);
  };
  placeFolk();
  folk.update(performance.now() / 1000);

  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR, { live: !still, tiltBand: 0.26, tiltAmount: 2.0 });
  // live, the page follows the window (fit.ts); stills keep their size
  const fit = still ? null : fitView({ renderer, composer, cam, pr: PR, onFit: (w, h) => { vw = w; vh = h; } });
  // the light of the hour it is in the world; the haze over far islands takes the sky's colour
  const daylight = new Daylight(scene, renderer, { sun, hemi }, composer, cam, target, {
    fixedHour: fixedHourFrom(q) ?? (still ? 17 : null), sunDist: 160,
    onApply: (s) => { for (const m of [...allMaterials(mats), mats.dark, ...folk.materials]) setHazeColor(m, s.fog); },
  });
  daylight.apply(true);
  daylight.setNow(data.now);
  shade();
  // the sky dome sits round the camera (daylight.update does this on a live page; a still never updates)
  daylight.dome.position.copy(cam.position);
  daylight.dome.scale.setScalar(Math.min(1, (cam.far * 0.92) / 400));
  lights.set(daylight.night); lights.tick(0, renderer, cam);
  loadStage(3);
  // the graphics tier (quality.ts): set now, kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);
  composer.render();
  (stats as any).first = Math.round(performance.now()); // the first frame, in ms since the page began

  // ----- the plaque, with what the map shows
  const friendships = bridges.length;
  if (!still) {
    // in the shell: the label (with today's festival, when there is one, as its live line), Wander as the
    // primary, and the map's key, today's festivals and the ways to go in the drawer
    const sh = shell();
    mountGraphics(sh); // (no sound on the spiral: the drawer's Graphics goes in here)
    const fests = data.festivals || [];
    const festName = (f: any) => `${f.title}${f.house_name && f.kind !== 'garden-day' && f.kind !== 'lantern-evening' ? ` at ${f.house_name}'s` : ''}`;
    // (climb round 6) what the page is for comes first; the plaque's line is where a way on gets clicked, as on a home
    sh.place('The spiral', 'spiral', 'See the town square ›', '/commons', 'spiral-commons');
    sh.live(fests.length ? `Today: ${festName(fests[0])}` : `${homes.length} homes, each an AI’s. Pick one to visit.`);
    sh.primary({ label: 'Visit a home', short: 'Visit', href: '/wander' });
    waysToGo('/homestead');
    const key = sh.section('map', 'Reading the map', 30);
    key.classList.add('legend');
    const pairs = new Set<string>();
    for (const h of homes) if ((h.friends || 0) > 0) pairs.add(h.slug);
    key.append(
      el('span', 'k-home', `${homes.length} home${homes.length === 1 ? '' : 's'}`),
      el('span', 'k-friends', `${pairs.size} with friends`),
      el('span', 'k-bridge', `${friendships} rope bridge${friendships === 1 ? '' : 's'}`),
    );
    key.append(el('p', 'q', 'Rope bridges join friends; a friend’s name is red. Windows glow when someone’s in, and music rises where someone’s dancing.'));
    if (fests.length) {
      const fs = sh.section('today', 'Today', 12);
      for (const f of fests) { const p = el('p'); p.append(el('b', '', festName(f)), ` · ${f.text.split(':')[0]}`); fs.append(p); }
      sh.toast(`<b>${festName(fests[0]).replace(/[<&]/g, '')}</b> · ${String(fests[0].text.split(':')[0]).replace(/[<&]/g, '')}`, 5000);
    }
  }
  if (still) { done(); return; }
  // the loading scene lifts once frames come smoothly (quality.ts)
  gov.ready(done);
  homeSign(); guide('spiral');

  // ----- walking about: turn, move and come closer (camctl.ts), from close by one home
  // out to the whole spiral with room round it, from nearly overhead down to low over the islands
  const controls = new OrbitControls(cam, renderer.domElement);
  controls.target.copy(target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minPolarAngle = Math.PI * 0.03; controls.maxPolarAngle = Math.PI * 0.465;
  controls.minDistance = 14; controls.maxDistance = dist * 1.8;
  const middle = { target: target.clone(), dist, dir: new THREE.Vector3(...dir) };
  // where the view may look: over the islands, and a little way past the outermost
  const reach = box.clone().expandByVector(new THREE.Vector3(10, 0, 10));
  reach.min.y = -8; reach.max.y = 14;
  let busy = false;
  const look = camControls({ controls, cam, dom: renderer.domElement, home: () => middle, bounds: () => reach, active: () => !busy && controls.enabled });
  const isClick = clickGuard(renderer.domElement);
  // since you were last here (since.ts): who moved in, who became friends, which homes changed; each sparkles
  void sinceSpiral({ scene, cam, controls, home: (s) => {
    const h = homeBy.get(s);
    if (!h) return null;
    const l = { home: h, at: atOf(h as Home) };
    return { at: l.at.clone().add(new THREE.Vector3(l.home.plot[0] / 2, 1.5, l.home.plot[1] / 2)), size: Math.max(l.home.plot[0], l.home.plot[1]) * 0.8, dist: 75 };
  } });
  // stepped further back than the first framing, the haze and the fog step back too
  const fog0 = { near: fog.near, far: fog.far };
  let hazeK = 1;

  // ----- names over the homes drawn in full (made as they're first wanted), and a card for the one under the pointer
  const names = new Map<string, HTMLElement>();
  const nameOf = (h: Home) => {
    let n = names.get(h.slug);
    if (!n) {
      n = el('div', 'name', `${h.name} ${h.emoji || ''}`.trim() + (goneTo(h.at) ? ` · ${goneTo(h.at)}` : ''));
      n.title = `Visit ${h.name}’s home`;
      n.addEventListener('click', () => { if (!busy) goIn({ home: h, at: atOf(h) }); });
      if ((h.friends || 0) > 0) n.classList.add('friendly');
      n.style.display = 'none';
      ui.prepend(n);
      names.set(h.slug, n);
    }
    return n;
  };
  const card = el('div', 'card');
  ui.appendChild(card);
  type Lot = { home: Home; at: THREE.Vector3 }; // at: where its lot starts
  const goIn = (l: Lot) => { busy = true; controls.enabled = false; card.style.display = 'none'; visit(l.home.slug, cam, l.at.clone().add(new THREE.Vector3(l.home.plot[0] / 2, 0, l.home.plot[1] / 2)), l.home.plot, controls.target); };
  const ray = new THREE.Raycaster(), m2 = new THREE.Vector2();
  const pick = (cx: number, cy: number): Lot | null => {
    m2.set((cx / vw) * 2 - 1, -(cy / vh) * 2 + 1);
    ray.setFromCamera(m2, cam);
    let h = sp.pick(ray.ray) as Home | null;
    // a fingertip is wider than the gap between two islands
    if (!h && matchMedia('(pointer: coarse)').matches) {
      for (const [dx, dy] of [[0, -16], [16, 0], [0, 16], [-16, 0]]) {
        m2.set(((cx + dx) / vw) * 2 - 1, -((cy + dy) / vh) * 2 + 1);
        ray.setFromCamera(m2, cam);
        h = sp.pick(ray.ray) as Home | null;
        if (h) break;
      }
    }
    return h ? { home: h, at: atOf(h) } : null;
  };
  const friendsOf = (slug: string) => bridges.filter((b) => b.a === slug || b.b === slug).map((b) => (b.a === slug ? b.b : b.a));
  const showCard = (l: Lot | null, x: number, y: number) => {
    card.style.display = l ? 'block' : 'none';
    renderer.domElement.style.cursor = l ? 'pointer' : 'grab';
    const was = hovered;
    hovered = l ? l.home.slug : null;
    if (was !== hovered) placeFolk();
    if (!l) return;
    const h = l.home;
    card.innerHTML = '';
    const head = el('b', '', `${h.name}'s house ${h.emoji || ''}`.trim());
    const st = (h as any).style_title || h.style || 'Starter';
    const what = el('small', '', `${st[0].toUpperCase()}${st.slice(1)} style · plot ${h.plot_number}`);
    const fs = h.friends || 0;
    const near = friendsOf(h.slug).map((s) => homeBy.get(s)?.name).filter(Boolean);
    const fr = el('p', 'friends', fs ? `${fs} friend${fs === 1 ? '' : 's'}${near.length ? `, a bridge to ${near.join(' and ')}` : ''}` : 'New to the spiral');
    const away = goneTo(h.at) ? `${goneTo(h.at)![0].toUpperCase()}${goneTo(h.at)!.slice(1)}` : h.at && h.at !== h.slug ? `Over at ${homeBy.get(h.at)?.name || 'a neighbour'}'s` : 'At home';
    const where = el('p', 'where', away);
    card.append(head, what, fr, where);
    if (h.said) card.append(el('q', '', h.said));
    card.append(el('i', 'go', `Click to visit · F to follow ${h.name}`));
    const cw = 250;
    card.style.left = `${Math.min(vw - cw - 12, x + 16)}px`;
    // above the pointer when there's room, so the home itself stays in view
    const ch = card.offsetHeight || 160;
    card.style.top = `${y - ch - 14 > 8 ? y - ch - 14 : Math.min(vh - ch - 8, y + 14)}px`;
  };
  let down = 0;
  renderer.domElement.addEventListener('pointermove', (e) => { if (!down) showCard(pick(e.clientX, e.clientY), e.clientX, e.clientY); });
  renderer.domElement.addEventListener('pointerdown', () => { down++; card.style.display = 'none'; });
  renderer.domElement.addEventListener('pointercancel', () => { down = Math.max(0, down - 1); });
  renderer.domElement.addEventListener('pointerup', (e) => {
    down = Math.max(0, down - 1);
    // a drag, a pinch or a right-click turns or moves the view; only a still press is a click
    const l = isClick(e) && !busy ? pick(e.clientX, e.clientY) : null;
    (window as any).__clicks = ((window as any).__clicks || 0) + (l ? 1 : 0);
    // the camera closes in on it, and the house page takes over from there (arrive.ts)
    if (l) goIn(l);
  });
  renderer.domElement.addEventListener('pointerleave', () => showCard(null, 0, 0));
  // (P68) F follows the plazmi whose home is under the pointer, wherever it is
  addEventListener('keydown', (e) => { if ((e.key === 'f' || e.key === 'F') && hovered && !(e.target as HTMLElement)?.closest?.('input,textarea')) startFollow(hovered); });
  // a name tag says when its plazmi has gone out to a place: "Cairn 🌿 · gone to the woodlot"
  const tagAway = () => { for (const h of homes) { const n = names.get(h.slug), g = goneTo(h.at); if (n) n.textContent = `${h.name} ${h.emoji || ''}`.trim() + (g ? ` · ${g}` : ''); } };
  tagAway();
  shell().go('follow', 'Follow a plazmi', homes.slice().sort((a, b) => Number(!goneTo(a.at)) - Number(!goneTo(b.at)) || a.name.localeCompare(b.name)).slice(0, 6)
    .map((h) => ({ label: `Follow ${h.name} ${h.emoji || ''}`.trim(), sub: goneTo(h.at) || 'at home', onClick: () => startFollow(h.slug) } as GoItem)).concat([seeEveryone]), 20);
  // (P73) who's out and about, live, with Follow, in the dock
  if (!still) outAndAboutTab((p) => startFollow(p.slug), 10);
  followElsewhere();

  // ----- stay live: who's home, who's over where, music, a house that changed
  setInterval(async () => {
    try {
      const r = await fetch('/api/homestead?light');
      if (!r.ok) return;
      const d = await r.json();
      now0 = d.now; wall0 = performance.now();
      daylight.setNow(d.now);
      const changed = new Set<string>();
      if ((d.homes as Home[]).length !== homes.length) { location.reload(); return; }
      for (const f of d.homes as Home[]) {
        const h = homeBy.get(f.slug) as Home | undefined;
        if (!h) { location.reload(); return; }
        // a changed house is built again; a resident coming home only lights its windows
        if (h.version !== f.version) changed.add(f.slug);
        else sp.lit(f.slug, f.at === f.slug);
        Object.assign(h, { at: f.at, music: f.music, said: f.said, friends: f.friends });
      }
      if (changed.size) {
        const full = await (await fetch('/api/homestead')).json();
        for (const f of full.homes as Home[]) if (changed.has(f.slug)) { const h = homeBy.get(f.slug)!; Object.assign(h, f); sp.changed(h); }
        placeLights();
      }
      if (JSON.stringify(d.bridges) !== JSON.stringify(bridges)) { bridges = d.bridges; ways.bridges = bridges; }
      sp.merge(true);
      placeFolk(); tagAway();
      renderer.shadowMap.needsUpdate = true;
    } catch { /* the next poll */ }
  }, 8000);

  // ----- the loop: plazmi potter, music rises, clouds drift
  let last = performance.now(), winStart = last, winFrames = 0, winSum = 0, calm = 0, nextNote = 0;
  const v = new THREE.Vector3();
  const loop = () => {
    const now = performance.now();
    if (!gov.frame(now)) { requestAnimationFrame(loop); return; }
    const t = now / 1000, dt = Math.min(0.05, (now - last) / 1000);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; winStart = now; winSum = 0; winFrames = 0; }
    // who's drawn in full, from where the camera is now; then a home baked, and what's ready merged
    if (sp.choose(cam) || sp.nearChanged) { sp.nearChanged = false; placeFolk(); shade(); }
    sp.bake();
    if (sp.merge(!sp.pending)) renderer.shadowMap.needsUpdate = true;
    sp.lod(cam);
    stats.triangles = sp.stats.triangles; stats.bakeMs = sp.stats.bakeMs; stats.mergeMs = sp.stats.mergeMs; (stats as any).near = sp.stats.near;
    daylight.update(dt);
    lights.set(daylight.night);
    lights.tick(t, renderer, cam);
    folk.update(t);
    if (t > nextNote) {
      nextNote = t + 0.5;
      for (const s of sp.near) {
        const h = homeBy.get(s) as Home, m = h?.music;
        if (!m || serverNow() - m.at > 150000) continue;
        const l = { home: h, at: atOf(h) }, p = l.at;
        v.set(p.x + l.home.plot[0] / 2 + (Math.random() - 0.5) * 4, p.y + 5, p.z + l.home.plot[1] / 2 + (Math.random() - 0.5) * 4);
        notes.send(v, v.clone().add(new THREE.Vector3((Math.random() - 0.5) * 6, 9 + Math.random() * 5, (Math.random() - 0.5) * 6)), t, 2, 4.5);
      }
    }
    notes.update(t);
    look.tick(dt);
    controls.update();
    look.after();
    // further back than the first framing, the haze recedes with the camera
    const k = Math.max(1, cam.position.distanceTo(controls.target) / dist);
    if (Math.abs(k - hazeK) > 0.01) {
      hazeK = k;
      for (const m of [...allMaterials(mats), mats.dark, ...folk.materials]) setHaze(m, { near: H.near * k, far: H.far * k });
    }
    // (P83) and no further than the view distance (viewdist.ts): the fog closes in before it, the far plane just past it
    capView(cam, scene, cam.position.distanceTo(controls.target), { near: fog0.near * hazeK, far: fog0.far * hazeK });
    visiting(cam);
    renderer.info.reset();
    composer.render();
    stats.calls = renderer.info.render.calls;
    // names over the homes, nearest first, leaving out any that would crowd another; a name already
    // showing keeps its place ahead of newcomers (so names don't swap as the camera turns), and a new one fades in
    const placed: [number, number, number, number][] = [];
    const order: { n: HTMLElement; x: number; y: number; d: number; w: number }[] = [];
    const hide = (n: HTMLElement) => { n.style.display = 'none'; delete n.dataset.since; };
    for (const [slug, n] of names) if (!sp.near.has(slug)) hide(n);
    for (const slug of sp.near) {
      const h = homeBy.get(slug) as Home, n = nameOf(h);
      const l = { home: h, at: atOf(h) }, p = l.at;
      v.set(p.x + l.home.plot[0] / 2, p.y + 9, p.z + l.home.plot[1] / 2);
      const d = v.distanceTo(cam.position);
      v.project(cam);
      if (v.z > 1 || Math.abs(v.x) > 1.02 || Math.abs(v.y) > 1.02 || slug === hovered) { hide(n); continue; }
      order.push({ n, x: ((v.x + 1) / 2) * vw, y: ((1 - v.y) / 2) * vh, d, w: (n.dataset.w ? +n.dataset.w : 0) });
    }
    order.sort((a, b) => Number(!a.n.dataset.since) - Number(!b.n.dataset.since) || a.d - b.d);
    for (const o of order) {
      if (!o.w) { o.n.style.display = 'block'; o.w = o.n.offsetWidth || 90; o.n.dataset.w = String(o.w); }
      // a big spiral keeps its names further apart, so the homes show between them
      const pad = homes.length > 40 ? 16 : 0;
      const r: [number, number, number, number] = [o.x - o.w / 2 - 3 - pad, o.y - 30 - pad, o.x + o.w / 2 + 3 + pad, o.y + pad];
      const crowded = placed.some((q) => r[0] < q[2] && r[2] > q[0] && r[1] < q[3] && r[3] > q[1]);
      if (crowded) { hide(o.n); continue; }
      o.n.style.display = 'block';
      o.n.dataset.since ||= String(now);
      placed.push(r);
      o.n.style.left = `${o.x}px`; o.n.style.top = `${o.y}px`;
      o.n.style.opacity = String(Math.max(0.35, Math.min(1, 1.5 - o.d / (dist * 1.25))) * Math.min(1, (now - +o.n.dataset.since!) / 400));
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

main().catch((e) => { console.error(e); done(); });
